import { useState, useRef, useEffect } from 'react';
import { styled, Menu, MenuItem } from '@mui/material';
import AppTextInput from 'components/FormElements/AppTextInput';

type SearchableMenuProps = {
  options: Array<any>;
  onSelect: (option: any) => void;
  getOptionLabel: (option: any) => string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  id: string;
  searchPlaceholder?: string;
};

const FilterContainer = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(4),
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
  '& > div': {
    width: '100%',
    textAlign: 'center',
  },
}));

const SearchableMenu = ({
  options,
  onSelect,
  getOptionLabel,
  anchorEl,
  onClose,
  id,
  searchPlaceholder,
}: SearchableMenuProps) => {
  const [filterValue, setFilterValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (anchorEl) {
      // Small delay to ensure the input is mounted
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [anchorEl]);

  const filteredOptions = options.filter((option) =>
    getOptionLabel(option).toLowerCase().includes(filterValue.toLowerCase()),
  );

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      id={id}
      sx={{
        '& .MuiMenu-list': {
          paddingTop: 0,
        },
      }}
    >
      <FilterContainer>
        <AppTextInput
          inputRef={inputRef}
          label={searchPlaceholder ?? 'Search'}
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </FilterContainer>
      {filteredOptions.map((option) => (
        <MenuItem key={getOptionLabel(option)} onClick={() => onSelect(option)}>
          {getOptionLabel(option)}
        </MenuItem>
      ))}
      {filteredOptions.length === 0 && (
        <MenuItem disabled>No options found</MenuItem>
      )}
    </Menu>
  );
};

export default SearchableMenu;
